/**
 * ExportButton —— D 看板导出按钮 (Phase 5-1 + 5-5 共享组件)
 *
 * 行为：
 *  1. 用 PermissionGate(DASHBOARD_READ) 包裹 —— 没权限的角色直接不渲染按钮；
 *  2. 点击 → 调 exportDashboard(kind, filter) → Blob + filename；
 *  3. 调 triggerDownload(blob, filename) 触发浏览器下载；
 *  4. 全过程 loading + 异常时 antd.message.error。
 *
 * 注意：服务端 watermark 由 admin-service watermark.service 负责（隐藏列 +
 * 文件名 hash）；UI 端不重复实现，只负责触发下载。
 */
import { useState } from 'react';
import { Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { PermissionGate } from '../PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import { exportDashboard, type DashboardKind, type ExportFilter } from '../../api/dashboard';
import { triggerDownload } from '../../utils/export-watermark';

export interface ExportButtonProps {
  kind: DashboardKind;
  filter: ExportFilter;
  /** 按钮文案，默认 "导出 CSV" */
  label?: string;
  testId?: string;
}

export function ExportButton({
  kind,
  filter,
  label = '导出 CSV',
  testId,
}: ExportButtonProps) {
  const [loading, setLoading] = useState(false);

  async function onClick() {
    setLoading(true);
    try {
      const { blob, filename } = await exportDashboard(kind, filter);
      triggerDownload(blob, filename);
      message.success(`已导出 ${filename}`);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        (err as Error).message ??
        '导出失败';
      message.error(`导出失败: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <PermissionGate point={PERMISSIONS.DASHBOARD_READ}>
      <Button
        icon={<DownloadOutlined />}
        onClick={onClick}
        loading={loading}
        data-testid={testId ?? 'dashboard-export-btn'}
      >
        {label}
      </Button>
    </PermissionGate>
  );
}
